import { Translation } from '@ps/web-shared-components';
import { numberRange, numbersOnly } from '@ps/web-shared-validators';
import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import Alert from '../Alert/Alert';
import CTAButton from '../CTAButton/CTAButton';
import TextInput from '../TextInput/TextInput';

import {
  TXTCLI_Hours,
  TXTCLI_Please_enter_a_number_between__ELL,
  TXTWCL_Daily_max_N0_hours,
  TXTWCL_Daily_playing_time,
  TXTWCL_Monthly_playing_time,
  TXTWCL_Montlhy_max_N0_hours,
  TXTWCL_Weekly_max_N0_hours,
  TXTWCL_Weekly_playing_time,
  TXTWCL_Whole_numbers_onlyX_No_decimal_ELL,
  TXTWCL_You_need_to_set_at_least_1_lim_ELL
} from './ResponsibleGamingTimeLimits.messages.json';
import {
  alert,
  button,
  container,
  inputAdornment,
  inputContainer
} from './ResponsibleGamingTimeLimits.scss';

interface Props {
  fieldName: string;
}

export const dailyDynamicLimit = (weeklyLimit: number, monthlyLimit: number) => {
  const maxDailyHours = 23;
  const weeklyLimitClean = isNaN(weeklyLimit) ? maxDailyHours : weeklyLimit;
  const monthlyLimitClean = isNaN(monthlyLimit) ? maxDailyHours : monthlyLimit;
  return Math.min(Math.max(maxDailyHours, weeklyLimitClean, monthlyLimitClean), maxDailyHours);
};

export const weeklyDynamicLimit = (dailyLimit: number) => (!isNaN(dailyLimit) ? dailyLimit : 1);
export const monthlyDynamicLimit = (dailyLimit: number, weeklyLimit: number) =>
  weeklyLimit ? weeklyLimit : dailyLimit ? dailyLimit : 1;

const ResponsibleGamingTimeLimits: React.FC<Props> = ({ fieldName }) => {
  const form = useForm();
  const [noLimitsSet, setNoLimitsSet] = useState(false);
  const defaultSystemCurrency = useSelector(select.account.getLimitCurrency);

  const {
    input: { value: dailyLimit }
  } = useField(`${fieldName}.daily.limit`, { subscription: { value: true } });
  const {
    input: { value: weeklyLimit }
  } = useField(`${fieldName}.weekly.limit`, { subscription: { value: true } });
  const {
    input: { value: monthlyLimit }
  } = useField(`${fieldName}.monthly.limit`, { subscription: { value: true } });
  useField(`${fieldName}.currency`, {
    subscription: { value: true },
    initialValue: defaultSystemCurrency
  });

  useEffect(() => {
    if (noLimitsSet && (dailyLimit || weeklyLimit || monthlyLimit)) {
      setNoLimitsSet(false);
    }
  }, [noLimitsSet, dailyLimit, weeklyLimit, monthlyLimit]);

  const handleSubmit = useCallback(() => {
    if (!dailyLimit && !weeklyLimit && !monthlyLimit) {
      setNoLimitsSet(true);
      return;
    }
    form.submit();
  }, [dailyLimit, form, monthlyLimit, weeklyLimit]);

  return (
    <div className={container}>
      {noLimitsSet && (
        <Alert variant="error" className={alert}>
          <Translation {...TXTWCL_You_need_to_set_at_least_1_lim_ELL} />
        </Alert>
      )}

      <div className={inputContainer}>
        <TextInput
          id={`${fieldName}.daily.limit`}
          name={`${fieldName}.daily.limit`}
          type="tel"
          label={TXTWCL_Daily_playing_time}
          helperMessage={<Translation {...TXTWCL_Daily_max_N0_hours} values={{ 0: 23 }} />}
          maxLength={2}
          validators={[
            numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL, true),
            numberRange(
              1,
              dailyDynamicLimit(weeklyLimit, monthlyLimit),
              {
                ...TXTCLI_Please_enter_a_number_between__ELL,
                values: {
                  0: 1,
                  1: dailyDynamicLimit(weeklyLimit, monthlyLimit)
                }
              },
              true
            )
          ]}
        />
        <span className={inputAdornment}>
          <Translation {...TXTCLI_Hours} />
        </span>
      </div>
      <div className={inputContainer}>
        <TextInput
          id={`${fieldName}.weekly.limit`}
          name={`${fieldName}.weekly.limit`}
          type="tel"
          label={TXTWCL_Weekly_playing_time}
          helperMessage={<Translation {...TXTWCL_Weekly_max_N0_hours} values={{ 0: 167 }} />}
          maxLength={3}
          validators={[
            numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL, true),
            numberRange(
              dailyLimit ? weeklyDynamicLimit(dailyLimit) : 1,
              167,
              {
                ...TXTCLI_Please_enter_a_number_between__ELL,
                values: { 0: dailyLimit ? weeklyDynamicLimit(dailyLimit) : 1, 1: 167 }
              },
              true
            )
          ]}
        />
        <span className={inputAdornment}>
          <Translation {...TXTCLI_Hours} />
        </span>
      </div>
      <div className={inputContainer}>
        <TextInput
          id={`${fieldName}.monthly.limit`}
          name={`${fieldName}.monthly.limit`}
          type="tel"
          label={TXTWCL_Monthly_playing_time}
          helperMessage={<Translation {...TXTWCL_Montlhy_max_N0_hours} values={{ 0: 719 }} />}
          maxLength={3}
          validators={[
            numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL, true),
            numberRange(
              monthlyDynamicLimit(dailyLimit, weeklyLimit),
              719,
              {
                ...TXTCLI_Please_enter_a_number_between__ELL,
                values: {
                  0: monthlyDynamicLimit(dailyLimit, weeklyLimit),
                  1: 719
                }
              },
              true
            )
          ]}
        />
        <span className={inputAdornment}>
          <Translation {...TXTCLI_Hours} />
        </span>
      </div>
      <CTAButton type="button" onClick={handleSubmit} className={button} />
    </div>
  );
};

export default ResponsibleGamingTimeLimits;
